/**
 * Configurações > Planilhas: a partir de quando a REVISÃO vem antes da
 * APROVAÇÃO no executivo (utils/fluxoExecutivo.js).
 *
 * A data vale pelo EXECUTIVO REALIZADO de cada ambiente: o que terminou antes
 * segue na ordem antiga até sair do escritório. Vazio = ordem antiga para
 * todos. O ERP só lê a planilha, então a trava de verdade fica nela: quando
 * há data, a tela mostra a validação de dados para aplicar na coluna
 * APROVAÇÃO.
 */
import React, { useEffect, useState } from "react";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import { useDados } from "../../../context/DadosContext";
import { salvarOrdemExecutivo } from "../../../services/planilhaLegado";
import { dataLocalDe, formatarDataLocal } from "../../../utils/dinheiro";

const PERMISSAO_CONFIGURAR = "planilha_legado:configurar";
const PERMISSAO_VER = "planilha_legado:view";

/** Fórmula da validação de dados (Excel em português) para a coluna APROVAÇÃO. */
export function formulaDaTrava(iso) {
  const corte = dataLocalDe(iso);
  if (!corte) return "";
  const data = `DATA(${corte.getFullYear()};${corte.getMonth() + 1};${corte.getDate()})`;
  return `=OU($R5<>"";$L5<${data})`;
}

function TravaNaPlanilha({ iso }) {
  const formula = formulaDaTrava(iso);
  if (!formula) return null;
  return (
    <details className="set-row__desc" style={{ marginTop: 8 }}>
      <summary style={{ cursor: "pointer" }}>Travar na planilha (Excel)</summary>
      <ol style={{ margin: "8px 0 0", paddingLeft: 18, display: "grid", gap: 4 }}>
        <li>Na aba GERENCIAL, selecione a coluna APROVAÇÃO a partir da linha 5 (P5 até o fim).</li>
        <li>Dados &gt; Validação de dados &gt; Permitir: Personalizado.</li>
        <li>
          Fórmula: <code style={{ userSelect: "all" }}>{formula}</code>
        </li>
        <li>Na aba Alerta de erro, escreva: &quot;Preencha a REVISÃO antes da APROVAÇÃO.&quot;</li>
      </ol>
      <p style={{ margin: "8px 0 0" }}>
        A trava vale para o que for digitado; colar valores passa por cima dela. Por isso o ERP também
        avisa: a linha fica na Revisão com o selo &quot;Fora de ordem&quot; e entra no sino.
      </p>
    </details>
  );
}

export default function OrdemDoExecutivo() {
  const { hasPermission, request } = useAuth();
  const { revisaoAntesDesde, recarregarOrdemExecutivo } = useDados();
  const [campo, setCampo] = useState(revisaoAntesDesde || "");
  const podeConfigurar = hasPermission(PERMISSAO_CONFIGURAR);
  const podeVer = podeConfigurar || hasPermission(PERMISSAO_VER);

  useEffect(() => {
    setCampo(revisaoAntesDesde || "");
  }, [revisaoAntesDesde]);

  if (!podeVer) return null;

  async function salvar(valor) {
    await salvarOrdemExecutivo(request, valor);
    await recarregarOrdemExecutivo();
  }

  const vigente = revisaoAntesDesde
    ? `Revisão antes da Aprovação para executivos finalizados a partir de ${formatarDataLocal(revisaoAntesDesde)}.`
    : "Ordem antiga para todos: Aprovação e depois Revisão.";

  return (
    <div className="set-row">
      <div>
        <div className="set-row__lbl">Ordem do executivo</div>
        <div className="set-row__desc">
          A partir desta data a Revisão vem antes da Aprovação. Vale pelo dia em que o executivo foi
          finalizado: o que terminou antes segue na ordem antiga. A Revisão vence 2 dias úteis depois
          do executivo.
        </div>
      </div>
      <div className="set-row__control">
        <div className="set-row__desc" style={{ marginBottom: 8 }}>{vigente}</div>
        {podeConfigurar ? (
          <>
            <div className="input-group">
              <label className="input-label" htmlFor="revisao-antes-desde">
                Revisão antes da Aprovação a partir de
              </label>
              <input
                id="revisao-antes-desde"
                className="input"
                type="date"
                value={campo}
                onChange={(e) => setCampo(e.target.value)}
              />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              {revisaoAntesDesde ? (
                <AsyncButton
                  variant="secondary"
                  onClick={() => salvar(null)}
                  successMessage="Voltou para a ordem antiga."
                >
                  Voltar à ordem antiga
                </AsyncButton>
              ) : null}
              <AsyncButton
                variant="primary"
                onClick={() => salvar(campo)}
                successMessage="Ordem do executivo salva."
                disabled={!campo || campo === revisaoAntesDesde}
              >
                Salvar
              </AsyncButton>
            </div>
            <TravaNaPlanilha iso={revisaoAntesDesde} />
          </>
        ) : null}
      </div>
    </div>
  );
}
